(() => {
  if (customElements.get('grz-reviews')) return;

  class GripzReviews extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-review-viewport]');
      this.track = this.querySelector('[data-review-track]');
      this.group = this.querySelector('[data-review-group]');
      if (!this.viewport || !this.track || !this.group) return;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.scheduleRefresh();
      this.onVisibilityChange = () => this.updateVisibility();
      this.motion.addEventListener('change', this.onMotionChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.updateVisibility();
      }, { rootMargin: '80px 0px' });
      this.intersectionObserver.observe(this);
      this.resizeObserver = new ResizeObserver(([entry]) => {
        if (this.viewportWidth !== entry.contentRect.width) {
          this.viewportWidth = entry.contentRect.width;
          this.scheduleRefresh();
        }
      });
      this.resizeObserver.observe(this.viewport);
      this.scheduleRefresh();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.refreshFrame);
      this.motion?.removeEventListener('change', this.onMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.clearClones();
      this.classList.remove('is-ready', 'is-visible');
    }

    updateVisibility() {
      this.classList.toggle('is-visible', Boolean(this.inView && !document.hidden));
    }

    scheduleRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }

    clearClones() {
      this.querySelectorAll('[data-review-clone]').forEach(clone => clone.remove());
    }

    clone(node) {
      const clone = node.cloneNode(true);
      clone.setAttribute('data-review-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(block => block.removeAttribute('data-shopify-editor-block'));
      return clone;
    }

    refresh() {
      if (!this.isConnected) return;
      this.classList.remove('is-ready');
      this.clearClones();
      if (this.motion.matches || this.dataset.autoplay === 'false') return;

      const cards = Array.from(this.group.children);
      const groupWidth = this.group.getBoundingClientRect().width;
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      if (!cards.length || groupWidth <= 0 || viewportWidth <= 0) return;

      const repeats = Math.min(20, Math.max(1, Math.ceil((viewportWidth + 1) / groupWidth)));
      const fragment = document.createDocumentFragment();
      for (let i = 1; i < repeats; i += 1) {
        cards.forEach(card => fragment.appendChild(this.clone(card)));
      }
      this.group.appendChild(fragment);
      const cycleWidth = this.group.getBoundingClientRect().width;
      this.track.appendChild(this.clone(this.group));
      const speed = Math.max(10, Math.min(100, Number(this.dataset.speed) || 30));
      this.style.setProperty('--grz-review-duration', `${cycleWidth / speed}s`);
      this.classList.add('is-ready');
      this.updateVisibility();
    }
  }

  customElements.define('grz-reviews', GripzReviews);
})();
